'use client';

import React, { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DropdownOption } from '@/types/DropdownOption';

import {
	CAL_DISPLAY_TYPE_MOBILE,
	CAL_DISPLAY_TYPE_MONTHLY,
	CAL_DISPLAY_TYPE_WEEKLY,
} from '@/constants/calendar';
import {
	EVENT_FILTER_KEY_FORMAT,
	EVENT_FILTER_KEY_LOCATION,
	EVENT_FILTER_KEY_PREMIUM,
	EVENT_FILTER_KEY_PRODUCT_LINE,
} from '@/constants/events';
import {
	ICON_CALENDAR_MONTHLY,
	ICON_CALENDAR_WEEKLY,
	ICON_FILTER,
} from '@/constants/icons';
import { PL_TYPES_ENABLED } from '@/constants/product';
import { SURFACE_EVENTS } from '@/constants/surfaces';
import {
	TX_ALL,
	TX_EVENTS_ADDITIONAL_FILTERS,
	TX_EVENTS_CALENDAR_MONTHLY,
	TX_EVENTS_CALENDAR_WEEKLY,
	TX_EVENTS_FORMAT,
	TX_EVENTS_MORE_FILTERS,
	TX_EVENTS_PREMIUM_EVENT,
	TX_EVENTS_PREMIUM_ONLY,
	TX_LOCATION,
	TX_PRODUCT_LINE,
  	TX_VIEW,
} from '@/constants/strings';

import useProductLines from '@/hooks/useProductLines';
import { visibleLocations } from '@/utils/locations';

import useStore from '@/hooks/useStore';

import ProductLine from '@/models/product-lines/ProductLine';

import { getOrderedPlayMenu } from '@/utils/product';

import Dropdown from '@/components/Input/Dropdown';
import Icon from '@/components/Icons/Icon';
import Toggle from '@/components/Input/Toggle';

import styles from './_styles/EventCalendarControls.module.scss';


// Prop types
interface EventCalendarControlsProps {
	adminTheme: boolean;
	displayMode: string;
	filters: Record<string, any>;
	setDisplayMode: (displayMode: string) => void;
	setFilter: (key: string, val: any, cb?: () => any) => void;
	width: number;
}

export const EventCalendarControls = (props: EventCalendarControlsProps): JSX.Element => {
  
	// Props
	const {
		adminTheme,
		displayMode,
		filters,
		setDisplayMode,
		setFilter,
		width,
	} = props;

	// Store
	const { store } = useStore();

	// Local state
	const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);

	// Accessing product lines
	const {
		lines: enabledLines,
		fetchLines: fetchEnabledLines,
	} = useProductLines(PL_TYPES_ENABLED);

	// Internationalization
	const { t } = useTranslation();

	// Constants
	const MID_BREAK = 1200;
	const isMidSizedView = width <= MID_BREAK;

	const isMobileView = displayMode === CAL_DISPLAY_TYPE_MOBILE;

	const viewDropdownOptions: DropdownOption[] = [
		{
			display: TX_EVENTS_CALENDAR_WEEKLY,
			value: CAL_DISPLAY_TYPE_WEEKLY,
		},
		{
			display: TX_EVENTS_CALENDAR_MONTHLY,
			value: CAL_DISPLAY_TYPE_MONTHLY,
		},
	];
	
	// Methods
	const toggleAdditionalFilters = () => {
		setFiltersExpanded((prev) => !prev)
	}

	const getProductLineOptions = (): DropdownOption[] => {

		const plOptions = [{
			display: t(TX_ALL),
			value: '',
		}];

		getOrderedPlayMenu(enabledLines).forEach((pl) => {
			plOptions.push({
				display: pl.name,
				value: pl.permalink,
			});
		});
		return plOptions;
	}

	const changeProductLine = (evt: React.ChangeEvent<HTMLSelectElement>) => {
		setFilter(EVENT_FILTER_KEY_PRODUCT_LINE, evt.target.value, () => {
			setFilter(EVENT_FILTER_KEY_FORMAT, '');
		});
	}

	const getFilterValue = (key: string) => {
		return filters[key] || '';
	}

	const selectedProductLine = (getFilterValue(EVENT_FILTER_KEY_PRODUCT_LINE) || '')
		? enabledLines.find((pl) => pl.permalink === getFilterValue(EVENT_FILTER_KEY_PRODUCT_LINE))
		: null;

	const showFormat = !!(selectedProductLine?.eventFormats && selectedProductLine.eventFormats.length > 0);

	const getFormatOptions = (): DropdownOption[] => {

		const options = [{
			display: TX_ALL,
			value: '',
		}];

		selectedProductLine?.eventFormats.forEach((format) => {
			options.push({
				display: format.name,
				value: format.key,
			});
		});
		return options;
	}

	const changeFormat = (evt: React.ChangeEvent<HTMLSelectElement>) => {
		setFilter(EVENT_FILTER_KEY_FORMAT, evt.target.value);
	}

	const getLocationOptions = (): DropdownOption[] => {

		const options = [{
			display: TX_ALL,
			value: '',
		}];

		visibleLocations(store?.locations ?? [], SURFACE_EVENTS).filter(l => l.publicUuid !== null).forEach((loc) => {
			options.push({
				display: loc.name,
				value: loc.publicUuid as string,
			});
		});
		return options;
	}

	const changeLocation = (evt: React.ChangeEvent<HTMLSelectElement>) => {
		const location = store?.locations.find((loc) => loc.publicUuid === evt.target.value);
		setFilter(EVENT_FILTER_KEY_LOCATION, location || null);
	}

	const changePremium = () => {
		setFilter(EVENT_FILTER_KEY_PREMIUM, !getFilterValue(EVENT_FILTER_KEY_PREMIUM));
	}

	// Effects
	useEffect(() => {
		fetchEnabledLines();
	}, [fetchEnabledLines])

	// Render
	return (
		<div className={`${styles.EventCalendarControls} ${adminTheme ? styles.adminTheme : ''}`}>
			<div className={styles.eccLiner}>
				<div className={`${styles.eccControlRow} ${isMobileView ? styles.mobile : ''}`}>
					<div className={`${styles.eccFilters} ${isMobileView ? styles.mobileFilters : ''}`}>
						<div className={styles.eccFilterWrapper}>
							<div className={styles.eccFilterLabel}>{t(TX_PRODUCT_LINE)}</div>
							<div className={styles.eccFilterInput}>
								<Dropdown 
									className={styles.eccFilterDropdown}
									options={getProductLineOptions()}
									name={t(TX_PRODUCT_LINE)}
									value={getFilterValue(EVENT_FILTER_KEY_PRODUCT_LINE)}
									onChange={changeProductLine}
									noTranslate={true} />
							</div>
						</div>
						{showFormat && (
							<div className={`${styles.eccFilterWrapper} ${isMidSizedView ? styles.supressed : ''}`}>
								<div className={styles.eccFilterLabel}>{t(TX_EVENTS_FORMAT)}</div>
								<div className={styles.eccFilterInput}>
									<Dropdown 
										className={styles.eccFilterDropdown}
										options={getFormatOptions()}
										name={t(TX_EVENTS_FORMAT)}
										value={getFilterValue(EVENT_FILTER_KEY_FORMAT)}
										onChange={changeFormat} />
								</div>
							</div>
						)}
						{store?.multiLocation && (
							<div className={`${styles.eccFilterWrapper} ${isMidSizedView ? styles.supressed : ''}`}>
								<div className={styles.eccFilterLabel}>{t(TX_LOCATION)}</div>
								<div className={styles.eccFilterInput}>
									<Dropdown 
										className={styles.eccFilterDropdown}
										options={getLocationOptions()}
										name={t(TX_LOCATION)}
										value={getFilterValue(EVENT_FILTER_KEY_LOCATION)?.publicUuid || ''}
										onChange={changeLocation} />
								</div>
							</div>
						)}
						<div className={`${styles.eccFilterWrapper} ${isMidSizedView ? styles.supressed : ''}`}>
							<div className={styles.eccFilterLabel}>{t(TX_EVENTS_PREMIUM_EVENT)}</div>
							<div className={styles.eccFilterInput}>
								<Toggle 
									checked={!!getFilterValue(EVENT_FILTER_KEY_PREMIUM)}
									name={t(TX_EVENTS_PREMIUM_EVENT)}
									onToggle={changePremium}
									trueValue={TX_EVENTS_PREMIUM_ONLY}
									falseValue={TX_ALL} />
							</div>
						</div>
						<div className={`${styles.eccFilterToggle} ${isMidSizedView ? styles.visible : ''}`} onClick={toggleAdditionalFilters}>
							<div className={styles.eccFilterIcon}>
								<Icon value={ICON_FILTER} />
							</div>
							<div className={styles.eccFilterToggleLabel}>{t(TX_EVENTS_MORE_FILTERS)}</div>
						</div>
					</div>
					{!isMobileView && (
						<div className={styles.eccViewControls}>
							<div className={styles.eccViewDropdownLabel}>
								{t(TX_VIEW)}
							</div>
							<div className={styles.eccViewDropdownWrapper}>
								<Dropdown 
									className={styles.eccViewDropdown}
									options={viewDropdownOptions}
									name={t(TX_VIEW)}
									value={displayMode}
									onChange={(evt) => setDisplayMode(evt.target.value)} />
							</div>
							<div className={styles.eccViewIconGroup}>
								<div className={styles.eccViewIconWrapper} onClick={() => setDisplayMode(CAL_DISPLAY_TYPE_WEEKLY)}>
									<Icon 
										value={ICON_CALENDAR_WEEKLY} 
										iconClass={`${styles.viewIcon} ${displayMode === CAL_DISPLAY_TYPE_WEEKLY ? styles.selected : ''}`} />
								</div>
								<div className={styles.eccViewIconWrapper} onClick={() => setDisplayMode(CAL_DISPLAY_TYPE_MONTHLY)}>
									<Icon 
										value={ICON_CALENDAR_MONTHLY} 
										iconClass={`${styles.viewIcon} ${displayMode === CAL_DISPLAY_TYPE_MONTHLY ? styles.selected : ''}`} />
								</div>
							</div>
						</div>
					)}
				</div>
				<div className={`${styles.eccAdditionalFilters} ${isMidSizedView ? styles.available : ''} ${filtersExpanded && !isMobileView ? styles.expanded : ''}`}>
					<div className={styles.additionalLiner}>
						<div className={styles.additionalTitleWrapper}>
							<div className={styles.additionalTitle}>{t(TX_EVENTS_ADDITIONAL_FILTERS)}</div>
						</div>
						{showFormat && (
							<div className={styles.eccFilterWrapper}>
								<div className={styles.eccFilterLabel}>{t(TX_EVENTS_FORMAT)}</div>
								<div className={styles.eccFilterInput}>
									<Dropdown 
										className={styles.eccFilterDropdown}
										options={getFormatOptions()}
										name={t(TX_EVENTS_FORMAT)}
										value={getFilterValue(EVENT_FILTER_KEY_FORMAT)}
										onChange={changeFormat} />
								</div>
							</div>
						)}
						{store?.multiLocation && (
							<div className={styles.eccFilterWrapper}>
								<div className={styles.eccFilterLabel}>{t(TX_LOCATION)}</div>
								<div className={styles.eccFilterInput}>
									<Dropdown 
										className={styles.eccFilterDropdown}
										options={getLocationOptions()}
										name={t(TX_LOCATION)}
										value={getFilterValue(EVENT_FILTER_KEY_LOCATION)?.publicUuid || ''}
										onChange={changeLocation} />
								</div>
							</div>
						)}
						<div className={styles.eccFilterWrapper}>
							<div className={styles.eccFilterLabel}>{t(TX_EVENTS_PREMIUM_EVENT)}</div>
							<div className={styles.eccFilterInput}>
								<Toggle 
									checked={getFilterValue(EVENT_FILTER_KEY_PREMIUM)}
									name={t(TX_EVENTS_PREMIUM_EVENT)}
									onToggle={changePremium}
									trueValue={TX_EVENTS_PREMIUM_ONLY}
									falseValue={TX_ALL} />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default EventCalendarControls;
